import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'

function DeleteAccountModal({
  isOpen,
  onClose,
  onConfirm,
  account
}) {
  const { t } = useTranslation('settings')
  const [usernameInput, setUsernameInput] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [recoveryUrl, setRecoveryUrl] = useState('')
  const [showRecoveryUrl, setShowRecoveryUrl] = useState(false)

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setUsernameInput('')
      setShowRecoveryUrl(false)
      setRecoveryUrl('')
      setIsDeleting(false)
    }
  }, [isOpen])

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isDeleting) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, isDeleting, onClose])

  // Auto-focus username input when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        const input = document.getElementById('username-confirmation-input')
        if (input) input.focus()
      }, 100)
    }
  }, [isOpen])

  if (!isOpen || !account) return null

  const expectedUsername = `@${account.username}`
  const isConfirmed = usernameInput === expectedUsername
  const isInvalid = usernameInput.length > 0 && usernameInput !== expectedUsername

  const handleDelete = async () => {
    if (!isConfirmed) return

    setIsDeleting(true)
    try {
      const result = await onConfirm(account.id, usernameInput)
      if (result?.recovery_url) {
        setRecoveryUrl(result.recovery_url)
        setShowRecoveryUrl(true)
      } else {
        // Close modal after successful deletion
        setTimeout(() => onClose(), 2000)
      }
    } catch (error) {
      console.error('Failed to delete account:', error)
      setIsDeleting(false)
    }
  }

  const copyRecoveryUrl = () => {
    navigator.clipboard.writeText(recoveryUrl)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={!isDeleting ? onClose : undefined}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="relative bg-white rounded-xl shadow-xl max-w-lg w-full mx-auto max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200"
        role="dialog"
        aria-labelledby="delete-modal-title"
        aria-describedby="delete-modal-description"
      >
        {!showRecoveryUrl ? (
          <>
            <div className="p-6">
              {/* Icon */}
              <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-red-100 mb-4">
                <ExclamationTriangleIcon className="h-7 w-7 text-red-600" aria-hidden="true" />
              </div>

              {/* Title */}
              <h3
                id="delete-modal-title"
                className="text-lg font-semibold text-gray-900 text-center mb-2"
              >
                {t('accounts.delete.modal.title')}
              </h3>

              {/* Account info */}
              <div className="flex items-center justify-center space-x-3 mb-4">
                {account.profile_picture_url && (
                  <img
                    src={account.profile_picture_url}
                    alt={account.username}
                    className="w-10 h-10 rounded-full"
                  />
                )}
                <span className="font-medium text-gray-900">@{account.username}</span>
              </div>

              {/* Warning message */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm font-medium text-red-900 mb-2">
                  {t('accounts.delete.modal.warning.title')}
                </p>
                <ul className="space-y-1 text-sm text-red-800">
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>{t('accounts.delete.modal.warning.item1')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>{t('accounts.delete.modal.warning.item2')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>{t('accounts.delete.modal.warning.item3')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>{t('accounts.delete.modal.warning.item4')}</span>
                  </li>
                </ul>
              </div>

              {/* Grace period notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <span className="font-medium">{t('accounts.delete.modal.gracePeriod.title')}</span>
                  {' '}
                  {t('accounts.delete.modal.gracePeriod.message')}
                </p>
              </div>

              {/* Username confirmation */}
              <div className="space-y-2">
                <label
                  htmlFor="username-confirmation-input"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t('accounts.delete.modal.confirmation.label')}
                </label>
                <p className="text-xs text-gray-500">
                  {t('accounts.delete.modal.confirmation.help', { username: expectedUsername })}
                </p>
                <input
                  id="username-confirmation-input"
                  type="text"
                  value={usernameInput}
                  onChange={(e) => setUsernameInput(e.target.value)}
                  placeholder={expectedUsername}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    isInvalid
                      ? 'border-red-300 focus:ring-red-500'
                      : isConfirmed
                      ? 'border-green-300 focus:ring-green-500'
                      : 'border-gray-300 focus:ring-primary-500'
                  }`}
                  aria-invalid={isInvalid}
                  aria-describedby={isInvalid ? 'username-error' : undefined}
                  disabled={isDeleting}
                  autoComplete="off"
                />
                {isInvalid && (
                  <p id="username-error" className="text-xs text-red-600 mt-1">
                    {t('accounts.delete.modal.confirmation.error')}
                  </p>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 px-6 py-4 bg-gray-50 rounded-b-xl">
              <button
                onClick={onClose}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 border border-gray-300 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('accounts.delete.modal.button.cancel')}
              </button>
              <button
                onClick={handleDelete}
                disabled={!isConfirmed || isDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center space-x-2"
              >
                {isDeleting && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                <span>
                  {isDeleting
                    ? t('accounts.delete.modal.button.deleting')
                    : t('accounts.delete.modal.button.confirm')
                  }
                </span>
              </button>
            </div>
          </>
        ) : (
          /* Recovery URL display */
          <div className="p-6">
            <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-green-100 mb-4">
              <svg className="h-7 w-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 text-center mb-4">
              {t('accounts.delete.modal.success.title')}
            </h3>

            <div className="space-y-4">
              <p className="text-sm text-gray-600 text-center">
                {t('accounts.delete.modal.success.message')}
              </p>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm font-medium text-amber-900 mb-2">
                  {t('accounts.delete.modal.success.recoveryUrl.title')}
                </p>
                <p className="text-xs text-amber-800 mb-3">
                  {t('accounts.delete.modal.success.recoveryUrl.description')}
                </p>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={recoveryUrl}
                    readOnly
                    className="flex-1 px-3 py-2 text-xs font-mono bg-white border border-amber-300 rounded-md"
                  />
                  <button
                    onClick={copyRecoveryUrl}
                    className="px-3 py-2 text-xs font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-md transition-colors"
                  >
                    {t('accounts.delete.modal.success.recoveryUrl.copy')}
                  </button>
                </div>
              </div>

              <p className="text-xs text-gray-500 text-center">
                {t('accounts.delete.modal.success.note')}
              </p>
            </div>

            <div className="mt-6 flex justify-center">
              <button
                onClick={onClose}
                className="px-6 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                {t('accounts.delete.modal.success.button')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

DeleteAccountModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  account: PropTypes.shape({
    id: PropTypes.string.isRequired,
    username: PropTypes.string.isRequired,
    profile_picture_url: PropTypes.string
  })
}

export default DeleteAccountModal